import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Typography, Grid, Paper, CircularProgress,
    ToggleButton, ToggleButtonGroup, Chip, useTheme,
} from '@mui/material';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import {
    Assignment as AssignmentIcon,
    Timer as TimerIcon,
    TrendingUp as TrendingUpIcon,
    Visibility as MttdIcon,
    Block as FpIcon,
    Computer as HostIcon,
    Person as UserIcon,
} from '@mui/icons-material';
import {
    RootState, Ticket, TicketPriority, TicketStatus, TicketCategory, IntegrationAlert,
} from '../../types';
import { fetchTickets } from '../../store/slices/ticketsSlice';
import { fetchIntegrationAlerts } from '../../store/slices/integrationDataSlice';
import { AppDispatch } from '../../store';
import LineChart from '../../components/graphics/LineChart';
import PieChart from '../../components/graphics/PieChart';
import { colorUtils } from '../../theme';

// ── Constants ─────────────────────────────────────────────────────────────────

type TimeRange = '30d' | '90d' | '6m' | '12m';

const SLA_HOURS: Record<TicketPriority, number> = {
    [TicketPriority.CRITICAL]: 4,
    [TicketPriority.HIGH]:     24,
    [TicketPriority.MEDIUM]:   72,
    [TicketPriority.LOW]:      168,
};

const PRIORITY_ORDER: TicketPriority[] = [
    TicketPriority.CRITICAL, TicketPriority.HIGH, TicketPriority.MEDIUM, TicketPriority.LOW,
];

const CATEGORY_LABELS: Record<TicketCategory, string> = {
    [TicketCategory.MALWARE]:             'Malware',
    [TicketCategory.PHISHING]:            'Phishing',
    [TicketCategory.UNAUTHORIZED_ACCESS]: 'Unauth. Access',
    [TicketCategory.DATA_BREACH]:         'Data Breach',
    [TicketCategory.DENIAL_OF_SERVICE]:   'DoS',
    [TicketCategory.INSIDER_THREAT]:      'Insider Threat',
    [TicketCategory.POLICY_VIOLATION]:    'Policy Violation',
    [TicketCategory.SUSPICIOUS_ACTIVITY]: 'Suspicious Activity',
    [TicketCategory.OTHER]:               'Other',
};

// ── Time helpers ──────────────────────────────────────────────────────────────

const getRangeStart = (range: TimeRange): Date => {
    const now = new Date();
    switch (range) {
        case '30d': { const d = new Date(now); d.setDate(d.getDate() - 30);   return d; }
        case '90d': { const d = new Date(now); d.setDate(d.getDate() - 90);   return d; }
        case '6m':  { return new Date(now.getFullYear(), now.getMonth() - 6,  1); }
        case '12m': { return new Date(now.getFullYear(), now.getMonth() - 12, 1); }
    }
};

/** Start of the period immediately before the current one (equal duration). */
const getPrevRangeStart = (range: TimeRange): Date => {
    const now = new Date();
    switch (range) {
        case '30d': { const d = new Date(now); d.setDate(d.getDate() - 60);   return d; }
        case '90d': { const d = new Date(now); d.setDate(d.getDate() - 180);  return d; }
        case '6m':  { return new Date(now.getFullYear(), now.getMonth() - 12, 1); }
        case '12m': { return new Date(now.getFullYear(), now.getMonth() - 24, 1); }
    }
};

/** Time-bucket boundaries for sparkline and chart data. */
const getBuckets = (range: TimeRange) => {
    const now = new Date();
    const useWeeks  = range === '30d';
    const numBuckets = range === '30d' ? 4 : range === '90d' ? 3 : range === '6m' ? 6 : 12;

    return Array.from({ length: numBuckets }, (_, i) => {
        let start: Date, end: Date, label: string;
        if (useWeeks) {
            end   = new Date(now); end.setDate(now.getDate() - (numBuckets - 1 - i) * 7);
            start = new Date(end); start.setDate(end.getDate() - 7);
            label = start.toLocaleString('default', { month: 'short', day: 'numeric' });
        } else {
            start = new Date(now.getFullYear(), now.getMonth() - (numBuckets - 1 - i), 1);
            end   = new Date(start.getFullYear(), start.getMonth() + 1, 0, 23, 59, 59);
            label = start.toLocaleString('default', {
                month: 'short',
                ...(numBuckets > 6 && { year: '2-digit' }),
            });
        }
        return { start, end, label };
    });
};

// ── Metric helpers ────────────────────────────────────────────────────────────

const formatHours = (hours: number | null): string => {
    if (hours === null) return '—';
    if (hours < 1)  return '< 1h';
    if (hours < 24) return `${hours.toFixed(1)}h`;
    return `${(hours / 24).toFixed(1)}d`;
};

const computeAvgMttr = (tickets: Ticket[]): number | null => {
    const resolved = tickets.filter(t => t.resolved_at);
    if (resolved.length === 0) return null;
    const totalMs = resolved.reduce(
        (sum, t) => sum + (new Date(t.resolved_at!).getTime() - new Date(t.created_at).getTime()), 0
    );
    return totalMs / resolved.length / 3_600_000;
};

const computeSlaCompliance = (tickets: Ticket[]): number | null => {
    const resolved = tickets.filter(t => t.resolved_at);
    if (resolved.length === 0) return null;
    const within = resolved.filter(t => {
        const due = t.due_date
            ? new Date(t.due_date)
            : new Date(new Date(t.created_at).getTime() + SLA_HOURS[t.priority] * 3_600_000);
        return new Date(t.resolved_at!) <= due;
    });
    return Math.round((within.length / resolved.length) * 100);
};

/**
 * MTTD = avg(detected_at − occurred_at) across alerts linked to these tickets.
 * Requires backend to populate occurred_at and detected_at on IntegrationAlert.
 */
const computeAvgMttd = (tickets: Ticket[], integrationAlerts: IntegrationAlert[]): number | null => {
    const ticketIds = new Set(tickets.map(t => t.id));
    const relevant  = integrationAlerts.filter(
        a => a.ticket_id && ticketIds.has(a.ticket_id) && a.occurred_at && a.detected_at
    );
    if (relevant.length === 0) return null;
    const totalMs = relevant.reduce((sum, a) => {
        const diff = new Date(a.detected_at!).getTime() - new Date(a.occurred_at!).getTime();
        return sum + Math.max(0, diff);
    }, 0);
    return totalMs / relevant.length / 3_600_000;
};

/**
 * Noise / false-positive rate = % of tickets with status FALSE_POSITIVE.
 * Requires backend to support TicketStatus.FALSE_POSITIVE ("false_positive").
 */
const computeFpRate = (tickets: Ticket[]): number | null => {
    if (tickets.length === 0) return null;
    const fp = tickets.filter(t => t.status === TicketStatus.FALSE_POSITIVE).length;
    return Math.round((fp / tickets.length) * 100);
};

/** Period-over-period percentage delta. Returns null when previous = 0. */
const computeDelta = (current: number | null, previous: number | null): number | null => {
    if (current === null || previous === null || previous === 0) return null;
    return Math.round(((current - previous) / previous) * 100);
};

/** Top 5 hostnames and usernames from alerts linked to these tickets. */
const computeTopAssets = (tickets: Ticket[], integrationAlerts: IntegrationAlert[]) => {
    const ticketIds = new Set(tickets.map(t => t.id));
    const relevant  = integrationAlerts.filter(a => a.ticket_id && ticketIds.has(a.ticket_id));

    const hostCounts: Record<string, number> = {};
    const userCounts: Record<string, number> = {};
    for (const a of relevant) {
        if (a.hostname) hostCounts[a.hostname] = (hostCounts[a.hostname] || 0) + 1;
        if (a.username) userCounts[a.username] = (userCounts[a.username] || 0) + 1;
    }

    const ranked = (counts: Record<string, number>) =>
        Object.entries(counts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5)
            .map(([name, count]) => ({ name, count }));

    return { hostnames: ranked(hostCounts), usernames: ranked(userCounts) };
};

const getVolumeSeries = (tickets: Ticket[], range: TimeRange) =>
    getBuckets(range).map(({ start, end, label }) => ({
        name: label,
        Opened:   tickets.filter(t => { const d = new Date(t.created_at); return d >= start && d <= end; }).length,
        Resolved: tickets.filter(t => {
            if (!t.resolved_at) return false;
            const d = new Date(t.resolved_at);
            return d >= start && d <= end;
        }).length,
    }));

// ── Sub-components ────────────────────────────────────────────────────────────

const StatCard: React.FC<{
    label: string;
    value: string | number;
    sub?: string;
    icon: React.ElementType;
    color: string;
    delta?: number | null;
    /** true = higher is better (e.g. SLA%); false = lower is better (MTTR, FP rate). */
    deltaIsGoodWhenPositive?: boolean;
    sparkData?: number[];
    /** When true, renders a "pending data" state instead of the metric value. */
    unavailable?: boolean;
    /** Explanation shown in the unavailable state. */
    unavailableNote?: string;
}> = ({ label, value, sub, icon: Icon, color, delta, deltaIsGoodWhenPositive = true, sparkData, unavailable, unavailableNote }) => {
    const theme = useTheme();
    const isPositive = delta !== null && delta !== undefined && delta > 0;
    const isNegative = delta !== null && delta !== undefined && delta < 0;
    const deltaIsGood =
        delta === null || delta === undefined || delta === 0 ? null
        : (isPositive && deltaIsGoodWhenPositive) || (isNegative && !deltaIsGoodWhenPositive);
    const deltaColor =
        deltaIsGood === null      ? theme.palette.text.tertiary
        : deltaIsGood             ? theme.palette.severity.low
        :                           theme.palette.severity.critical;

    const sparkChartData = sparkData?.map(v => ({ v })) ?? [];
    const gradId = `spark-${label.replace(/\W+/g, '-').toLowerCase()}`;

    if (unavailable) {
        return (
            <Paper elevation={0} sx={{
                p: 2.5, borderRadius: 1.5, height: '100%',
                backgroundColor: theme.palette.background.paper,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{
                        width: 36, height: 36, borderRadius: 1.5, flexShrink: 0, mt: 0.25,
                        backgroundColor: colorUtils.withOpacity(theme.palette.text.tertiary, 0.07),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Icon sx={{ color: theme.palette.text.tertiary, fontSize: 18, opacity: 0.5 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.71rem', color: theme.palette.text.secondary, lineHeight: 1.3 }}>
                            {label}
                        </Typography>
                        <Typography sx={{ fontSize: '0.67rem', color: theme.palette.text.tertiary, mt: 0.75, lineHeight: 1.5 }}>
                            {unavailableNote ?? 'Data not yet available'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper elevation={0} sx={{
            p: 2.5, borderRadius: 1.5, height: '100%',
            backgroundColor: theme.palette.background.paper,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{
                    width: 36, height: 36, borderRadius: 1.5, flexShrink: 0, mt: 0.25,
                    backgroundColor: colorUtils.withOpacity(color, 0.12),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Icon sx={{ color, fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.1 }}>
                        {value}
                    </Typography>
                    <Typography sx={{ fontSize: '0.71rem', color: theme.palette.text.secondary, mt: 0.2 }}>
                        {label}
                    </Typography>
                    {sub && (
                        <Typography sx={{ fontSize: '0.67rem', color: theme.palette.text.tertiary, mt: 0.1 }}>
                            {sub}
                        </Typography>
                    )}
                    {delta !== null && delta !== undefined && (
                        <Typography sx={{ fontSize: '0.67rem', color: deltaColor, mt: 0.5, fontWeight: 600 }}>
                            {isPositive ? '↑' : isNegative ? '↓' : '—'} {Math.abs(delta)}% vs prev period
                        </Typography>
                    )}
                </Box>
                {sparkChartData.length > 1 && (
                    <Box sx={{ width: 56, height: 38, flexShrink: 0, alignSelf: 'center' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={sparkChartData} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                                <defs>
                                    <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%"  stopColor={color} stopOpacity={0.35} />
                                        <stop offset="95%" stopColor={color} stopOpacity={0.03} />
                                    </linearGradient>
                                </defs>
                                <Area
                                    type="monotone" dataKey="v"
                                    stroke={color} strokeWidth={1.5}
                                    fill={`url(#${gradId})`}
                                    dot={false} isAnimationActive={false}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

const MttrRow: React.FC<{
    priority: TicketPriority; avgHours: number | null; count: number;
}> = ({ priority, avgHours, count }) => {
    const theme = useTheme();
    const slaH = SLA_HOURS[priority];
    const withinSla = avgHours !== null && avgHours <= slaH;
    const barPct = avgHours !== null ? Math.min(100, (avgHours / slaH) * 100) : 0;

    const priorityColors: Record<TicketPriority, string> = {
        [TicketPriority.CRITICAL]: theme.palette.severity.critical,
        [TicketPriority.HIGH]:     theme.palette.severity.high,
        [TicketPriority.MEDIUM]:   theme.palette.severity.medium,
        [TicketPriority.LOW]:      theme.palette.severity.low,
    };
    const color = priorityColors[priority];

    return (
        <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: theme.palette.text.primary, textTransform: 'capitalize' }}>
                        {priority}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.tertiary }}>
                        ({count} resolved)
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: avgHours !== null ? color : theme.palette.text.disabled }}>
                        {formatHours(avgHours)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: theme.palette.text.tertiary }}>
                        / {formatHours(slaH)}
                    </Typography>
                    {avgHours !== null && (
                        <Chip
                            label={withinSla ? '✓' : '!'}
                            size="small"
                            sx={{
                                height: 18, fontSize: '0.65rem', fontWeight: 700,
                                backgroundColor: withinSla
                                    ? colorUtils.withOpacity(theme.palette.severity.low, 0.15)
                                    : colorUtils.withOpacity(theme.palette.severity.critical, 0.15),
                                color: withinSla ? theme.palette.severity.low : theme.palette.severity.critical,
                                '& .MuiChip-label': { px: 0.75 },
                            }}
                        />
                    )}
                </Box>
            </Box>
            <Box sx={{ height: 4, borderRadius: 2, backgroundColor: theme.palette.background.inset, overflow: 'hidden' }}>
                <Box sx={{
                    height: '100%', borderRadius: 2, transition: 'width 0.6s ease',
                    width: `${barPct}%`,
                    backgroundColor: withinSla ? color : theme.palette.severity.critical,
                }} />
            </Box>
        </Box>
    );
};

const TopList: React.FC<{
    title: string;
    icon: React.ElementType;
    items: Array<{ name: string; count: number }>;
    color: string;
    emptyNote?: string;
}> = ({ title, icon: Icon, items, color, emptyNote }) => {
    const theme = useTheme();
    const maxCount = items[0]?.count ?? 1;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Icon sx={{ fontSize: 14, color: theme.palette.text.tertiary }} />
                <Typography sx={{
                    fontSize: '0.7rem', fontWeight: 700, color: theme.palette.text.tertiary,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                }}>
                    {title}
                </Typography>
            </Box>
            {items.length === 0 ? (
                <Typography sx={{ fontSize: '0.8rem', color: theme.palette.text.tertiary }}>
                    {emptyNote ?? 'No data available for this period'}
                </Typography>
            ) : (
                items.map(({ name, count }, i) => (
                    <Box key={name} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.4 }}>
                            <Typography sx={{
                                fontSize: '0.78rem', color: theme.palette.text.primary,
                                fontFamily: '"JetBrains Mono", monospace',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                flex: 1, mr: 1,
                            }}>
                                {i + 1}. {name}
                            </Typography>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: theme.palette.text.secondary, flexShrink: 0 }}>
                                {count}
                            </Typography>
                        </Box>
                        <Box sx={{ height: 3, borderRadius: 2, backgroundColor: theme.palette.background.inset, overflow: 'hidden' }}>
                            <Box sx={{
                                height: '100%', borderRadius: 2,
                                width: `${(count / maxCount) * 100}%`,
                                backgroundColor: colorUtils.withOpacity(color, 0.65),
                            }} />
                        </Box>
                    </Box>
                ))
            )}
        </Box>
    );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const ClientAnalyticsPage: React.FC = () => {
    const theme = useTheme();
    const dispatch = useDispatch<AppDispatch>();
    const { tickets, isLoading }       = useSelector((state: RootState) => state.tickets);
    const { alerts: integrationAlerts } = useSelector((state: RootState) => state.integrationData);
    const [range, setRange] = useState<TimeRange>('6m');

    useEffect(() => {
        dispatch(fetchTickets({}));
        dispatch(fetchIntegrationAlerts({ limit: 1000 }));
    }, [dispatch]);

    const rangeStart     = useMemo(() => getRangeStart(range),     [range]);
    const prevRangeStart = useMemo(() => getPrevRangeStart(range), [range]);

    const filtered = useMemo(() =>
        tickets.filter(t => new Date(t.created_at) >= rangeStart),
    [tickets, rangeStart]);

    const prevFiltered = useMemo(() =>
        tickets.filter(t => {
            const d = new Date(t.created_at);
            return d >= prevRangeStart && d < rangeStart;
        }),
    [tickets, prevRangeStart, rangeStart]);

    const stats = useMemo(() => {
        const buckets = getBuckets(range);

        const open     = filtered.filter(t => t.status !== TicketStatus.RESOLVED && t.status !== TicketStatus.CLOSED);
        const prevOpen = prevFiltered.filter(t => t.status !== TicketStatus.RESOLVED && t.status !== TicketStatus.CLOSED);

        const mttr     = computeAvgMttr(filtered);
        const prevMttr = computeAvgMttr(prevFiltered);
        const sla      = computeSlaCompliance(filtered);
        const prevSla  = computeSlaCompliance(prevFiltered);
        const mttd     = computeAvgMttd(filtered, integrationAlerts);
        const prevMttd = computeAvgMttd(prevFiltered, integrationAlerts);
        const fpRate   = computeFpRate(filtered);
        const prevFp   = computeFpRate(prevFiltered);

        const topAssets = computeTopAssets(filtered, integrationAlerts);

        const mttrByPriority = PRIORITY_ORDER.map(p => {
            const inPriority = filtered.filter(t => t.priority === p);
            return { priority: p, avgHours: computeAvgMttr(inPriority), count: inPriority.filter(t => t.resolved_at).length };
        });

        const priorityData = PRIORITY_ORDER.map(p => ({
            name: p.charAt(0).toUpperCase() + p.slice(1),
            value: filtered.filter(t => t.priority === p).length,
            color: ({
                [TicketPriority.CRITICAL]: theme.palette.severity.critical,
                [TicketPriority.HIGH]:     theme.palette.severity.high,
                [TicketPriority.MEDIUM]:   theme.palette.severity.medium,
                [TicketPriority.LOW]:      theme.palette.severity.low,
            } as Record<TicketPriority, string>)[p],
        })).filter(d => d.value > 0);

        const categoryData = Object.values(TicketCategory)
            .map(cat => ({ name: CATEGORY_LABELS[cat], value: filtered.filter(t => t.category === cat).length }))
            .filter(d => d.value > 0)
            .sort((a, b) => b.value - a.value);

        const volumeSeries = getVolumeSeries(tickets, range);

        // Per-bucket sparkline data for each KPI
        const bucketTickets = (b: { start: Date; end: Date }) =>
            tickets.filter(t => { const d = new Date(t.created_at); return d >= b.start && d <= b.end; });

        const totalSparkData = buckets.map(b => bucketTickets(b).length);
        const mttrSparkData  = buckets.map(b => computeAvgMttr(bucketTickets(b)) ?? 0);
        const slaSparkData   = buckets.map(b => computeSlaCompliance(bucketTickets(b)) ?? 0);
        const mttdSparkData  = buckets.map(b => computeAvgMttd(bucketTickets(b), integrationAlerts) ?? 0);
        const fpSparkData    = buckets.map(b => computeFpRate(bucketTickets(b)) ?? 0);

        return {
            total: filtered.length, prevTotal: prevFiltered.length,
            open:  open.length,     prevOpen:  prevOpen.length,
            mttr, prevMttr, sla, prevSla, mttd, prevMttd, fpRate, prevFp,
            mttrByPriority, priorityData, categoryData, volumeSeries, topAssets,
            totalSparkData, mttrSparkData, slaSparkData, mttdSparkData, fpSparkData,
        };
    }, [filtered, prevFiltered, tickets, integrationAlerts, range, theme]);

    if (isLoading && tickets.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    const slaColor =
        stats.sla === null ? theme.palette.text.secondary
        : stats.sla >= 90  ? theme.palette.severity.low
        : stats.sla >= 70  ? theme.palette.severity.medium
        :                    theme.palette.severity.critical;

    const fpColor =
        stats.fpRate === null  ? theme.palette.text.secondary
        : stats.fpRate === 0   ? theme.palette.severity.low
        : stats.fpRate <= 10   ? theme.palette.severity.medium
        :                        theme.palette.severity.critical;

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default, px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>

            {/* Header + time range */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                <Box>
                    <Typography variant="h1" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                        Analytics
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                        Incident metrics and trends
                    </Typography>
                </Box>
                <ToggleButtonGroup
                    value={range} exclusive onChange={(_, v) => v && setRange(v)} size="small"
                    sx={{
                        '& .MuiToggleButton-root': {
                            px: 2, py: 0.5, fontSize: '0.8rem', fontWeight: 600,
                            border: `1px solid ${theme.palette.border.main}`,
                            color: theme.palette.text.secondary,
                            '&.Mui-selected': {
                                backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.12),
                                color: theme.palette.primary.main,
                                borderColor: theme.palette.primary.main,
                            },
                        },
                    }}
                >
                    {(['30d', '90d', '6m', '12m'] as TimeRange[]).map(r => (
                        <ToggleButton key={r} value={r}>{r}</ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {/* ── KPI cards (6) ─────────────────────────────────────────── */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <StatCard
                        label="Incidents in period"
                        value={stats.total}
                        icon={AssignmentIcon}
                        color={theme.palette.primary.main}
                        delta={computeDelta(stats.total, stats.prevTotal)}
                        deltaIsGoodWhenPositive={false}
                        sparkData={stats.totalSparkData}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <StatCard
                        label="Currently open"
                        value={stats.open}
                        icon={AssignmentIcon}
                        color={theme.palette.severity.high}
                        delta={computeDelta(stats.open, stats.prevOpen)}
                        deltaIsGoodWhenPositive={false}
                        sparkData={stats.totalSparkData}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <StatCard
                        label="Avg. resolution time (MTTR)"
                        value={formatHours(stats.mttr)}
                        sub="mean time to resolve"
                        icon={TimerIcon}
                        color={theme.palette.severity.medium}
                        delta={computeDelta(stats.mttr, stats.prevMttr)}
                        deltaIsGoodWhenPositive={false}
                        sparkData={stats.mttrSparkData}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <StatCard
                        label="Avg. detection time (MTTD)"
                        value={formatHours(stats.mttd)}
                        sub="mean time to detect · from alert data"
                        icon={MttdIcon}
                        color={theme.palette.severity.critical}
                        delta={computeDelta(stats.mttd, stats.prevMttd)}
                        deltaIsGoodWhenPositive={false}
                        sparkData={stats.mttdSparkData}
                        unavailable={stats.mttd === null}
                        unavailableNote="Populates once alert timestamps are available from your integrations"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <StatCard
                        label="SLA compliance"
                        value={stats.sla !== null ? `${stats.sla}%` : '—'}
                        sub="resolved within contracted SLA"
                        icon={TrendingUpIcon}
                        color={slaColor}
                        delta={computeDelta(stats.sla, stats.prevSla)}
                        deltaIsGoodWhenPositive={true}
                        sparkData={stats.slaSparkData}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <StatCard
                        label="Noise / false positive rate"
                        value={stats.fpRate !== null ? `${stats.fpRate}%` : '—'}
                        sub="tickets closed as false positive"
                        icon={FpIcon}
                        color={fpColor}
                        delta={computeDelta(stats.fpRate, stats.prevFp)}
                        deltaIsGoodWhenPositive={false}
                        sparkData={stats.fpSparkData}
                        unavailable={stats.fpRate === null}
                        unavailableNote="No incidents in the selected period"
                    />
                </Grid>
            </Grid>

            {/* ── Volume over time + by priority ────────────────────────── */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, lg: 8 }}>
                    <Paper elevation={0} sx={{
                        p: 2.5, borderRadius: 1.5,
                        backgroundColor: theme.palette.background.paper,
                    }}>
                        <LineChart
                            data={stats.volumeSeries}
                            title="Incident volume"
                            subtitle={`Opened vs. resolved — last ${range}`}
                            height={280} variant="area"
                            dataKeys={[
                                { key: 'Opened',   label: 'Opened',   color: theme.palette.primary.main },
                                { key: 'Resolved', label: 'Resolved', color: theme.palette.severity.low },
                            ]}
                            showGrid showLegend showTooltip curved
                        />
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, lg: 4 }}>
                    <Paper elevation={0} sx={{
                        p: 2.5, borderRadius: 1.5, height: '100%',
                        backgroundColor: theme.palette.background.paper,
                    }}>
                        <PieChart
                            data={stats.priorityData}
                            title="By priority"
                            subtitle={`${stats.total} incidents`}
                            height={280} variant="donut"
                            showLegend showTooltip showCenterValue centerLabel="total"
                        />
                    </Paper>
                </Grid>
            </Grid>

            {/* ── By category + MTTR vs SLA ────────────────────────────── */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, lg: 5 }}>
                    <Paper elevation={0} sx={{
                        p: 2.5, borderRadius: 1.5,
                        backgroundColor: theme.palette.background.paper,
                    }}>
                        {stats.categoryData.length > 0 ? (
                            <PieChart
                                data={stats.categoryData}
                                title="By category"
                                height={300} variant="donut"
                                showLegend showTooltip
                            />
                        ) : (
                            <Box sx={{ p: 3, height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.tertiary }}>
                                    No data for this period
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, lg: 7 }}>
                    <Paper elevation={0} sx={{
                        p: 3, borderRadius: 1.5, height: '100%',
                        backgroundColor: theme.palette.background.paper,
                    }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: theme.palette.text.tertiary, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.5 }}>
                            MTTR vs. SLA threshold
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.tertiary, mb: 3 }}>
                            Average resolution time per priority · bar fills at 100% = SLA limit
                        </Typography>
                        {stats.mttrByPriority.map(({ priority, avgHours, count }) => (
                            <MttrRow key={priority} priority={priority} avgHours={avgHours} count={count} />
                        ))}
                    </Paper>
                </Grid>
            </Grid>

            {/* ── Top affected assets & accounts ───────────────────────── */}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, lg: 6 }}>
                    <Paper elevation={0} sx={{
                        p: 3, borderRadius: 1.5,
                        backgroundColor: theme.palette.background.paper,
                    }}>
                        <TopList
                            title="Top affected assets"
                            icon={HostIcon}
                            items={stats.topAssets.hostnames}
                            color={theme.palette.severity.high}
                            emptyNote="No hostname data in alert records for this period"
                        />
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                    <Paper elevation={0} sx={{
                        p: 3, borderRadius: 1.5,
                        backgroundColor: theme.palette.background.paper,
                    }}>
                        <TopList
                            title="Top affected accounts"
                            icon={UserIcon}
                            items={stats.topAssets.usernames}
                            color={theme.palette.severity.medium}
                            emptyNote="No username data in alert records for this period"
                        />
                    </Paper>
                </Grid>
            </Grid>

        </Box>
    );
};

export default ClientAnalyticsPage;
